import { useGetList, useTranslate } from "ra-core";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

import type { Contact, ContactNote } from "../types";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DashboardMetrics } from "./DashboardMetrics";
import { DashboardStepper } from "./DashboardStepper";
import { DealsChart } from "./DealsChart";
import { HotContacts } from "./HotContacts";
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";

const FADE_UP = "fadeUpIn 0.5s ease-out both";

const ScrollColumn = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("md:flex md:flex-col md:h-[480px]", className)}>
    {children}
  </div>
);

const DashboardSkeleton = () => (
  <div className="-mt-4 flex flex-col animate-pulse">
    <div className="flex py-6">
      <div className="flex flex-col gap-3 pr-8">
        <Skeleton className="h-14 w-28 rounded-none" />
        <Skeleton className="h-2 w-14 rounded-none" />
      </div>
      <div
        className="flex flex-col gap-3 px-8"
        style={{ borderLeft: "1px solid var(--nd-border)" }}
      >
        <Skeleton className="h-14 w-16 rounded-none" />
        <Skeleton className="h-2 w-20 rounded-none" />
      </div>
      <div
        className="flex flex-col gap-3 px-8"
        style={{ borderLeft: "1px solid var(--nd-border)" }}
      >
        <Skeleton className="h-14 w-16 rounded-none" />
        <Skeleton className="h-2 w-16 rounded-none" />
      </div>
    </div>
    <div style={{ borderTop: "1px solid var(--nd-border)" }} />
    <div className="py-10">
      <Skeleton className="h-[300px] w-full rounded-none" />
    </div>
    <div style={{ borderTop: "1px solid var(--nd-border)" }} />
    <div className="hidden md:grid grid-cols-12 gap-6 pt-8 pb-4">
      <div className="col-span-3 flex flex-col gap-3">
        <Skeleton className="h-3 w-20 rounded-none" />
        <Skeleton className="h-[200px] rounded-xl" />
      </div>
      <div className="col-span-6 flex flex-col gap-3">
        <Skeleton className="h-3 w-20 rounded-none" />
        <Skeleton className="h-[200px] rounded-xl" />
      </div>
      <div className="col-span-3 flex flex-col gap-3">
        <Skeleton className="h-3 w-20 rounded-none" />
        <Skeleton className="h-[200px] rounded-xl" />
      </div>
    </div>
  </div>
);

export const DashboardError = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
      <p className="font-nd-mono text-[11px] tracking-[0.08em] uppercase text-nd-text-disabled">
        {translate("ra.notification.http_error", {
          _: "Failed to load dashboard",
        })}
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.reload()}
      >
        {translate("ra.action.refresh", { _: "Retry" })}
      </Button>
    </div>
  );
};

export const Dashboard = () => {
  const {
    data: dataContact,
    total: totalContact,
    isPending: isPendingContact,
    isError: isErrorContact,
  } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 1 },
  });

  const {
    total: totalContactNotes,
    isPending: isPendingContactNotes,
    isError: isErrorNotes,
  } = useGetList<ContactNote>("contact_notes", {
    pagination: { page: 1, perPage: 1 },
  });

  const {
    total: totalDeal,
    isPending: isPendingDeal,
    isError: isErrorDeal,
  } = useGetList<Contact>("deals", {
    pagination: { page: 1, perPage: 1 },
  });

  const isPending = isPendingContact || isPendingContactNotes || isPendingDeal;

  if (isPending) {
    return <DashboardSkeleton />;
  }

  // Show error rather than the wrong stepper step when initial data fetches fail
  if (isErrorContact || isErrorNotes || isErrorDeal) {
    return <DashboardError />;
  }

  if (!totalContact) {
    return <DashboardStepper step={1} />;
  }

  if (!totalContactNotes) {
    return <DashboardStepper step={2} contactId={dataContact?.[0]?.id} />;
  }

  return (
    <div className="-mt-4 flex flex-col">
      {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}

      <div style={{ animation: FADE_UP }}>
        <DashboardMetrics totalContacts={totalContact ?? 0} />
        <div style={{ borderTop: "1px solid var(--nd-border)" }} />
      </div>

      {totalDeal ? (
        <div style={{ animation: `fadeUpIn 0.5s ease-out 0.08s both` }}>
          <div className="py-10">
            <DealsChart compact />
          </div>
          <div style={{ borderTop: "1px solid var(--nd-border)" }} />
        </div>
      ) : null}

      <div
        className="grid grid-cols-1 md:grid-cols-12 gap-6 pt-8 pb-4"
        style={{ animation: `fadeUpIn 0.5s ease-out 0.16s both` }}
      >
        <ScrollColumn className="md:col-span-3">
          <HotContacts />
        </ScrollColumn>
        <ScrollColumn className="md:col-span-6">
          <DashboardActivityLog />
        </ScrollColumn>
        <ScrollColumn className="md:col-span-3">
          <TasksList />
        </ScrollColumn>
      </div>
    </div>
  );
};
